import { useEffect, useRef, useState } from "react";
import { FaPlay, FaPause } from "react-icons/fa6";
import { formatBytes } from "./FileTile";

const AUDIO_PALETTE = {
  mp3: { btn: "bg-rose-500 hover:bg-rose-400", bar: "bg-rose-500" },
  wav: { btn: "bg-sky-500 hover:bg-sky-400", bar: "bg-sky-500" },
  flac: { btn: "bg-indigo-500 hover:bg-indigo-400", bar: "bg-indigo-500" },
  m4a: { btn: "bg-orange-500 hover:bg-orange-400", bar: "bg-orange-500" },
  aac: { btn: "bg-orange-500 hover:bg-orange-400", bar: "bg-orange-500" },
  ogg: { btn: "bg-violet-500 hover:bg-violet-400", bar: "bg-violet-500" },
  opus: { btn: "bg-violet-500 hover:bg-violet-400", bar: "bg-violet-500" },
  default: { btn: "bg-rose-500 hover:bg-rose-400", bar: "bg-rose-500" },
};

function fmtTime(s) {
  if (!Number.isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
}

function getExt(name) {
  const lower = (name || "").toLowerCase();
  const ext = lower.includes(".") ? lower.split(".").pop() : "";
  return /^[a-z0-9]+$/.test(ext) ? ext : "";
}

export function AudioTile({ file, mediaUrl }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onEnd = () => { setPlaying(false); setTime(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, []);

  function toggle(e) {
    e.stopPropagation();
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) {
      a.play().then(() => setPlaying(true)).catch(() => {});
    } else {
      a.pause();
      setPlaying(false);
    }
  }

  function stop(e) { e.stopPropagation(); }

  const pct = duration > 0 ? (time / duration) * 100 : 0;
  const ext = getExt(file.name);
  const palette = AUDIO_PALETTE[ext] || AUDIO_PALETTE.default;
  const extLabel = ext ? ext.toUpperCase() : "AUDIO";

  return (
    <div className="p-3 flex flex-col gap-2 w-[230px]">
      <p className="text-xs font-semibold text-zinc-800 dark:text-zinc-100 break-words leading-snug pointer-events-none">
        {file.name}
      </p>
      <div className="flex items-center gap-2.5">
        <button
          type="button"
          onClick={toggle}
          onPointerDown={stop}
          aria-label={playing ? "Pausar" : "Reproducir"}
          className={`w-9 h-9 rounded-full ${palette.btn} text-white flex items-center justify-center shrink-0 cursor-pointer transition-colors`}
        >
          {playing ? <FaPause className="w-3 h-3" /> : <FaPlay className="w-3 h-3 ml-0.5" />}
        </button>
        <div className="flex-1 flex flex-col gap-1 pointer-events-none min-w-0">
          <div className="h-1 rounded-full bg-zinc-200 dark:bg-zinc-700 overflow-hidden">
            <div
              className={`h-full ${palette.bar} transition-all duration-100`}
              style={{ width: `${pct}%` }}
            />
          </div>
          <p className="text-[10px] text-zinc-500 dark:text-zinc-400 tabular-nums tracking-wide">
            {extLabel} · {fmtTime(time)} / {fmtTime(duration)} · {formatBytes(file.size)}
          </p>
        </div>
      </div>
      <audio ref={audioRef} src={mediaUrl} preload="metadata" />
    </div>
  );
}
